import { Injectable, ErrorHandler, PLATFORM_ID, Inject, Optional } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { AUTH_INTERCEPTOR_CONFIG, AuthInterceptorConfig } from '../../auth/tokens/auth-config.token';

/** Substrings that match known benign DOM/browser errors (do not log to server). */
const SUPPRESSED_MESSAGE_SUBSTRINGS = [
  'insertBefore',
  'removeChild',
  'appendChild',
  'The object can not be found here',
  'ResizeObserver loop',
  /** Safari/WebKit autofill; missing in Facebook in-app browser / some WebViews */
  '_AutofillCallbackHandler',
  /** Google Maps (and similar embeds) use cross-origin iframes; browser blocks access — benign */
  'cross-origin frame',
  /** Microsoft Bing UET (bat.js) blocked or failed before UET global exists */
  'UET is not defined',
  'Unexpected EOF',
  /** Chrome/V8: truncated script download or JSON.parse on empty/incomplete body — not actionable without a stack trace */
  'Unexpected end of input',
  /** Injected/native bridge; only exists in some WebViews (e.g. affiliate / in-app browsers) */
  'PerformanceMonitoringJavascriptInterface',
  /** Kindly Chat (Pusher) WebSocket; network/adblock/firewall — not actionable in app code */
  'Pusher connection error',
  /** Firefox Reader / internal APIs; third-party or env assumes .reader exists */
  '__firefox__',
  /** Stripe.js v3 (or similar) on older Safari/WebKit — parse error for private class fields (#); not app code */
  'Bare private name',
  /** Meta / in-app browsers / injected scripts probe WKWebView bridge; often missing outside native shell */
  'window.webkit.messageHandlers',
  /** Extensions or injected scripts try to override navigator.userAgent; property is non-configurable */
  'Cannot redefine property: userAgent',
  /** WebView - specific errors that originate from third-party native layers e.g. Android WebView */
  'Java object is gone',
  /** MapLibre/Radar: WebGL context restore before style.imageManager is rebuilt (mobile tab background) */
  'imageManager',
  /** Microsoft Clarity (GTM): DOM node removed during SPA navigation before .sequence is read */
  'sequence',
  /** Third-party scripts (Klaviyo, Kindly, etc.) call navigator.permissions.query with unsupported names */
  'Permissions::query does not support this API',
  /** Google Reserve with Google (rwg_token) schema/microdata parser mutates readonly objects */
  'Attempted to assign to readonly property',
  /** Failed to execute 'createHTML' on 'TrustedTypePolicy': The provided callback is no longer runnable. */
  'TrustedTypePolicy',
  /** Kindly Chat (chat.kindlycdn.com) third-party widget fails to load its own webpack chunk — network/adblock/CDN; not actionable in app code */
  'KindlyChat'
];

// Check if the error message is a suppressed error message
function isSuppressedErrorMessage(message: string): boolean {
  return SUPPRESSED_MESSAGE_SUBSTRINGS.some((sub) => message.includes(sub));
}

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private isBrowser: boolean;
  private appName: string;
  private errorLogUrl: string | null;
  private readonly ERROR_LOG_SKIP_HOSTNAMES = ['localhost'];

  /**
   * Rate-limiting state to prevent runaway loops (e.g. an error firing on every
   * change-detection cycle) from flooding the error-log endpoint.
   * - Per-error dedup: same fingerprint is sent at most once per TTL window.
   * - Session cap: hard ceiling on total POSTs per page session.
   */
  private readonly ERROR_DEDUP_TTL_MS = 60_000;
  private readonly MAX_LOGS_PER_SESSION = 50;
  private readonly MAX_DEDUP_ENTRIES = 200;
  private readonly recentErrorTimestamps = new Map<string, number>();
  private totalErrorsLogged = 0;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(AUTH_INTERCEPTOR_CONFIG) private config: AuthInterceptorConfig
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.appName = this.config?.appName || 'way';
    this.errorLogUrl = this.config?.errorLogUrl || null;
    this.setupGlobalErrorHandlers();
    this.clearChunkReloadFlag();
  }

  /**
   * Clear the chunk reload flag after a short delay on app bootstrap.
   * Delay ensures we don't clear it before a chunk-error-triggered reload completes.
   * Clearing allows future deployments to trigger the one-time reload again.
   */
  private clearChunkReloadFlag(): void {
    if (!this.isBrowser || typeof localStorage === 'undefined') return;
    setTimeout(() => localStorage.removeItem('reload'), 2000);
  }

  /**
   * Setup global error handlers to catch ALL JS errors
   * including those inside RxJS subscriptions
   */
  private setupGlobalErrorHandlers(): void {
    if (!this.isBrowser) return;

    // Catch uncaught errors (including RxJS subscription errors)
    window.onerror = (message, source, lineno, colno, error) => {
      const msg = String(message);
      if (isSuppressedErrorMessage(msg)) {
        return true;
      }
      if (msg === 'Script error.' || (msg === 'Script error' && lineno === 0 && colno === 0)) {
        return true;
      }
      this.logError({
        errorStatusCode: 500,
        message: msg,
        stack: error?.stack || `${source}:${lineno}:${colno}`,
        type: 'Uncaught Error'
      });
      return false; // Let error propagate to console
    };

    // Catch unhandled promise rejections
    // window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    //   const reason = event.reason;
    //   let stack = 'No stack trace';
    //   if (reason instanceof Error && reason.stack) {
    //     stack = reason.stack;
    //   } else {
    //     try {
    //       stack = new Error('Unhandled rejection').stack || 'No stack trace';
    //     } catch (_) {}
    //   }

    //   this.logError({
    //     errorStatusCode: 500,
    //     message: reason instanceof Error ? reason.message : String(reason),
    //     stack,
    //     type: 'Unhandled Promise Rejection'
    //   });
    // };
  }

  /**
   * Angular's ErrorHandler - catches errors thrown in Angular zone
   */
  handleError(error: any): void {
    const errMessage = error?.message || '';
    if (isSuppressedErrorMessage(errMessage)) {
      return;
    }
    let errorMethodName: string;
    let errorIgnore = false;
    let reloadFlag = false;
    let errorStatusCode = 500;
    if (error instanceof HttpErrorResponse) {
      errorMethodName = 'HTTP error';
      errorStatusCode = error.status;
    } else if (error instanceof TypeError) {
      errorMethodName = 'Type error';
    } else if (error instanceof Error) {
      errorMethodName = 'General error';
    } else {
      errorMethodName = 'On Body Error';
    }

    const errMsg = JSON.stringify(error.message);
    if (errMsg) {
      if (errMsg.indexOf('idpiframe') > -1 || errMsg.indexOf('google') > -1 ||
          errMsg.indexOf('FB') > -1 || errMsg.indexOf('User cancelled login') > -1 ||
          errMsg.indexOf("setting 'defaultOpen'") > -1) {
        // Ignore known non-fatal errors:
        // - Google/FB social login errors
        // - ng-multiselect-dropdown compatibility issue with Angular 17+
        errorIgnore = true;
      } else if (errMsg.indexOf('ChunkLoadError') > -1 ||
                 errMsg.indexOf('dynamically imported module') > -1 ||
                 errMsg.indexOf('Importing a module script failed') > -1) {
        errorIgnore = true;
        if (this.isBrowser && typeof localStorage !== 'undefined') {
          if (localStorage.getItem('reload') !== 'true') {
            localStorage.setItem('reload', 'true');
            window.location.reload();
          }
        }
      }
    }

    if (this.isBrowser && !errorIgnore) {
      this.logError({
        errorStatusCode : errorStatusCode,
        message: error.message,
        stack: error.stack,
        type: errorMethodName
      }, reloadFlag);
    }

    if (errorIgnore) {
      return;
    }

    // Do not rethrow in browser: it would bubble as an uncaught exception and fire window.onerror,
    // which calls logError again with type "Uncaught Error" — duplicate rows in error logs.
    if (!this.isBrowser) {
      throw error;
    }
    console.error(error);
  }

  /**
   * Build a stable signature for an error so duplicates can be deduped.
   * Uses type + message + first non-empty stack line — enough to identify
   * the same throw site without being so specific that minor variations leak through.
   */
  private buildErrorFingerprint(info: { type: string; message: string; stack: string }): string {
    const firstStackLine = (info.stack || '')
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.length > 0) || '';
    return `${info.type}|${info.message}|${firstStackLine}`;
  }

  /**
   * Returns true when this error should NOT be POSTed to the error-log endpoint
   * because either (a) the same fingerprint was sent within the dedup TTL, or
   * (b) the session has already hit its hard cap.
   * Has the side effect of recording the timestamp when an error IS allowed through.
   */
  private shouldSkipErrorLog(info: { type: string; message: string; stack: string }): boolean {
    if (this.totalErrorsLogged >= this.MAX_LOGS_PER_SESSION) {
      return true;
    }

    const fingerprint = this.buildErrorFingerprint(info);
    const now = Date.now();
    const lastSentAt = this.recentErrorTimestamps.get(fingerprint);
    if (lastSentAt !== undefined && now - lastSentAt < this.ERROR_DEDUP_TTL_MS) {
      return true;
    }

    this.recentErrorTimestamps.set(fingerprint, now);

    // Bounded growth: sweep expired entries once the map exceeds its cap.
    if (this.recentErrorTimestamps.size > this.MAX_DEDUP_ENTRIES) {
      for (const [key, ts] of this.recentErrorTimestamps) {
        if (now - ts >= this.ERROR_DEDUP_TTL_MS) {
          this.recentErrorTimestamps.delete(key);
        }
      }
    }
    return false;
  }

  /**
   * Common method to log errors to backend
   */
  private logError(
    errorInfo: { errorStatusCode: number; message: string; stack: string; type: string },
    reloadAfter = false
  ): void {
    if (!this.isBrowser || !this.errorLogUrl || this.ERROR_LOG_SKIP_HOSTNAMES.includes(window.location.hostname)) return;

    if (this.shouldSkipErrorLog({
      type: errorInfo.type,
      message: errorInfo.message,
      stack: errorInfo.stack
    })) {
      return;
    }
    this.totalErrorsLogged++;

    const errMsgFlag = errorInfo.message?.includes('The quota has been exceeded') ||
                       errorInfo.message?.includes('QuotaExceededError');

    const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('userId') : null;
    const localStorageData = typeof localStorage !== 'undefined' ? JSON.stringify(localStorage) : '';

    const jsonData: any = {
      errorModule: reloadAfter ? `JavaScript - chunk - ${this.appName}` : `JavaScript - ${this.appName}`,
      errorMethodName: errorInfo.type,
      errorMethodInputName: 'browser ' + navigator.userAgent,
      errorSimpleMessage: errorInfo.message,
      errorStatusCode: errorInfo.errorStatusCode,
      errorStackTrace: errMsgFlag
        ? `localstorage= ${localStorageData} - location - ${location.href} ${errorInfo.stack}`
        : `${location.href} ${errorInfo.stack}`,
      userId: userId || ''
    };

    this.http.post(this.errorLogUrl, jsonData).subscribe({
      next: () => {
        if (reloadAfter) {
          window.location.reload();
        }
      },
      error: () => {
        // Silently fail - don't cause infinite error loop
      }
    });
  }
}

